/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { isEmptyObject } from '../components/OriginDestinationSelector/utils';
import {
  CABIN_CLASS_MAP,
  CabinClassKey,
  CabinClassType,
} from '../components/PassengerClass/types';
import { STATIONS } from '../config/constants';
import {
  StationDataObject,
  TripType,
  Passengers,
  ListingQueryParams,
} from '../types/FlightSearch.types';
import { PROVIDERS } from '../config/constants';
import { format as DATE_FORMAT, startOfDay } from 'date-fns';
import { getLocale } from './dateUtil';
import { parseEnv } from './EnvironmentURLs';
import EnvironmentURLs from './EnvironmentURLs';
import { ANALYTICS_CONSTANTS } from '../config/analytics';

/**
 * This checks if both the station objects are same or not, this is done by comparing various things like -
 * Are both of same type ?
 * If both city then check city code, city name and country name.
 * If both airport then check station code, airport name, city name and country name.
 * @param {StationDataObject} station1 - first station object.
 * @param {StationDataObject} station2 - second station object.
 * @returns {boolean} - checks if the station1 and station2 are same or not.
 */
export const same_station_object = (
  station1: StationDataObject | undefined,
  station2: StationDataObject | undefined,
) => {
  if (!station1 && !station2) return true;
  else if (!station1 || !station2) return false;
  const isStation1City = station1.StationType === STATIONS.TYPE.CITY;
  const isStation2City = station2.StationType === STATIONS.TYPE.CITY;

  //both are not of same type , both are neither city nor station
  if (isStation1City !== isStation2City) return false;
  //both are same type
  else {
    if (isStation1City)
      //both cities , check city code, city name and country name
      return (
        station1.CityCode === station2.CityCode &&
        station1.CityName === station2.CityName &&
        station1.CountryName === station2.CountryName
      );
    else {
      //both airports ,check airport name,city name and country name
      return (
        station1.StationCode === station2.StationCode &&
        station1.AirportName === station2.AirportName &&
        station1.CityName === station2.CityName &&
        station1.CountryName === station2.CountryName
      );
    }
  }
};

/**
 * Returns a user-friendly display name for a station.
 *
 * - If the station represents a city, it appends the "all airports" label
 *   e.g. "Kuala Lumpur (All Airports)"
 * - If the station represents a single airport, it falls back to
 *   AirportName → StationName
 *
 * @param station - Station data object or null/undefined
 * @param allAirportsLabel - Localized label for "All Airports"
 * @returns Formatted station display name or empty string if station is missing
 */
export const getHeaderStationDisplayName = (
  station: StationDataObject | undefined | null,
  allAirportsLabel: string,
): string => {
  if (!station) return '';

  let displayName: string;

  if (station.IsCity) {
    displayName = `${station.CityName} (${allAirportsLabel})`;
  } else {
    displayName = station.AirportName ?? station.StationName ?? '';
  }

  return displayName ?? '';
};

/**
 * Builds the header text displaying journey dates and guest(s) count.
 *
 * Examples:
 * - "Sun, 1 Nov - Fri, 6 Nov • 2 Guests"
 * - "Sun, 1 Nov • 1 Guest"
 *
 * Rules:
 * - If `departDate` is undefined, an empty string is returned
 * - Return date is shown only for ROUND_TRIP journeys
 * - Guest label switches between singular/plural based on total guest count
 * - Date formatting respects the provided locale
 *
 * @param departDate - Departure date (required to render header)
 * @param returnDate - Return date (used only for round trips)
 * @param adultCount - Number of adult passengers
 * @param infantCount - Number of infant passengers
 * @param childCount - Number of child passengers
 * @param guestSingularLabel - Localized singular label (e.g. "Guest")
 * @param guestPluralLabel - Localized plural label (e.g. "Guests")
 * @param tripType - Trip type (ONE_WAY or ROUND_TRIP)
 * @param locale - Locale used for date formatting (e.g. "en-GB")
 *
 * @returns Formatted journey and guest summary string
 */
export const getHeaderJourneyAndGuestDetails = (
  departDate: Date | undefined,
  returnDate: Date | undefined,
  adultCount: number,
  infantCount: number,
  childCount: number,
  guestSingularLabel: string,
  guestPluralLabel: string,
  tripType: string,
  locale: string,
) => {
  if (!departDate) {
    return '';
  }

  const totalGuests = adultCount + infantCount + childCount;

  const guestLabel = totalGuests === 1 ? guestSingularLabel : guestPluralLabel;

  const formatDate = (date: Date) => {
    if (typeof date === 'string') date = new Date(date);
    return DATE_FORMAT(date, 'EEE, d MMM', {
      locale: getLocale(locale),
    });
  };

  const departDateStr = formatDate(departDate);

  const shouldShowReturnDate = tripType === TripType.ROUND_TRIP && returnDate;

  const journeyStr = shouldShowReturnDate
    ? `${departDateStr} - ${formatDate(returnDate)}`
    : departDateStr;

  return `${journeyStr} • ${totalGuests} ${guestLabel}`;
};

export const stringToBool = (
  input: string | boolean | null | undefined,
): boolean => {
  if (typeof input === 'boolean') return input;
  if (typeof input === 'string') {
    return input.toLowerCase().trim() === 'true';
  }
  return false; // Default for null, undefined, or other types
};

/**
 * Safely parses date strings in DD/MM/YYYY format.
 * Returns undefined if parsing fails to avoid crashing the app.
 */
export const parseDeepLinkDate = (
  dateStr: string | undefined,
): Date | undefined => {
  if (!dateStr || typeof dateStr !== 'string') return undefined;

  // Handles DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, or YYYY/MM/DD
  const parts = dateStr.split(/[/,-]/);

  if (parts.length === 3) {
    let year: number;
    let month: number;
    let day: number;

    if (parts[0].length === 4) {
      // Handles YYYY-MM-DD or YYYY/MM/DD
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
      day = parseInt(parts[2], 10);
    } else {
      // Handles DD/MM/YYYY or DD-MM-YYYY
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
      year = parseInt(parts[2], 10);
    }

    const date = new Date(year, month, day);

    // Check if the resulting date is valid (e.g., handles 31/02/2024)
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // Fallback to standard constructor if format is different
  const fallbackDate = new Date(dateStr);
  return isNaN(fallbackDate.getTime()) ? undefined : fallbackDate;
};

// A depart date sourced from a deep link or a recent search can be stale by
// the time it's applied (bookmarked links, reopening the app days later).
// Snaps an expired departDate to today, then corrects returnDate against it
// using the given depart/return gap, so both land together in one update.
export const resolveNonExpiredDates = (
  rawDepartDate: Date | null | undefined,
  rawReturnDate: Date | null | undefined,
  returnDateGapInSeconds: number,
): { departDate?: Date; returnDate?: Date } => {
  let resolvedDepartDate: Date | undefined;
  if (rawDepartDate) {
    const today = startOfDay(new Date());
    resolvedDepartDate =
      startOfDay(rawDepartDate) < today ? today : rawDepartDate;
  }

  let resolvedReturnDate: Date | undefined;
  if (rawReturnDate) {
    const isReturnDateInvalid =
      resolvedDepartDate !== undefined && rawReturnDate < resolvedDepartDate;
    resolvedReturnDate = isReturnDateInvalid
      ? new Date(resolvedDepartDate!.getTime() + returnDateGapInSeconds * 1000)
      : rawReturnDate;
  }

  return { departDate: resolvedDepartDate, returnDate: resolvedReturnDate };
};

export const normalizeCabinClass = (input = ''): CabinClassKey | undefined => {
  try {
    const normalized = decodeURIComponent(input)
      .trim()
      .toLowerCase()
      .replace(/[-\s]+/g, '_');

    return normalized in CABIN_CLASS_MAP
      ? (normalized as CabinClassKey)
      : undefined;
  } catch {
    return undefined;
  }
};

export const getCabinClassType = (input?: string): CabinClassType => {
  const normalized = normalizeCabinClass(input ?? '');
  return normalized ? CABIN_CLASS_MAP[normalized] : CabinClassType.ECONOMY;
};

export const isDotrezFlight = (stationObj: StationDataObject) => {
  if (isEmptyObject(stationObj)) return false;

  if (stationObj?.IsCity) {
    const AAFlight = stationObj?.Stations?.some(
      (ele) =>
        ele?.Provider?.toUpperCase() === 'AAB' || stringToBool(ele?.AAFlight),
    );
    stationObj.AAFlight = stringToBool(AAFlight);
    return isAAFlight(stationObj);
  } else {
    return isAAFlight(stationObj);
  }
};

const isAAFlight = (stationObj: StationDataObject) => {
  const key: keyof StationDataObject = 'AAFlight';

  if (stationObj && key in stationObj && stringToBool(stationObj[key])) {
    return true;
  }
  return false;
};

export const isSameOriginAndDestination = (
  originObj?: StationDataObject,
  destinationObj?: StationDataObject,
) => {
  // Extract values
  const originCode = originObj?.StationCode?.toLowerCase()?.trim();
  const originCity = originObj?.CityCode?.toLowerCase()?.trim();
  const destCode = destinationObj?.StationCode?.toLowerCase()?.trim();
  const destCity = destinationObj?.CityCode?.toLowerCase()?.trim();

  // Perform the "Same" check
  // (Station A == Station B) OR (City A == City B)
  const isSameStation = !!originCode && originCode === destCode;
  const isSameCity = !!originCity && originCity === destCity;

  const isSame = isSameStation || isSameCity;
  return isSame;
};

export const stationOrCityCodeOrName = (
  stationObj: StationDataObject,
  keyName: string,
) => {
  if (stationObj?.IsCity) {
    return stationObj[
      keyName.toLowerCase() === 'name' ? 'CityName' : 'CityCode'
    ];
  } else {
    return stationObj[
      keyName.toLowerCase() === 'name' ? 'StationName' : 'StationCode'
    ];
  }
};

export const getJourneyDuration = (
  returnDate?: Date | null,
  departDate?: Date | null,
): number => {
  if (!returnDate || !departDate) return 0;

  const MS_PER_DAY = 1000 * 60 * 60 * 24;

  const start = new Date(departDate);
  const end = new Date(returnDate);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diff = end.getTime() - start.getTime();

  return diff <= 0 ? 0 : diff / MS_PER_DAY;
};

export const getProvider = (destinationObj: StationDataObject) => {
  const isAAB = (provider?: string) =>
    provider?.toUpperCase() === PROVIDERS.AAB;
  const stationType = destinationObj.StationType?.toUpperCase();

  const directRouteStationTypes = new Set<string>([
    STATIONS.TYPE.AIRPORT,
    STATIONS.TYPE.BUS,
    STATIONS.TYPE.FERRY,
  ]);

  // Case 1: Airport, need also to check for Bus & Ferry route
  if (stationType && directRouteStationTypes.has(stationType)) {
    return destinationObj?.Provider;
  }

  // Case 2: City
  const isCity = destinationObj.IsCity || stationType === STATIONS.TYPE.CITY;

  if (isCity) {
    const hasAABStation = destinationObj.Stations?.some((station) =>
      isAAB(station.Provider),
    );

    return hasAABStation ? PROVIDERS.AAB : PROVIDERS.OTA;
  }

  // Fallback (always return something)
  return PROVIDERS.AAB;
};

export const isEitherStationEmpty = (
  originObj?: StationDataObject,
  destinationObj?: StationDataObject,
): boolean => {
  return (
    !originObj ||
    !destinationObj ||
    isEmptyObject(originObj) ||
    isEmptyObject(destinationObj)
  );
};

export const dropPaxToSnap = (passengers: Passengers, MAX_PAX: number = 6) => {
  const pax = {
    adult: 0,
    child: 0,
    infant: 0,
  };
  if (!passengers) return pax;
  const flightPax =
    passengers && passengers.adult + passengers.child + passengers.infant;
  if (flightPax <= MAX_PAX) return passengers;

  if (passengers.adult >= MAX_PAX) {
    pax.adult = MAX_PAX;
    return pax;
  }
  pax.adult = passengers.adult;
  if (passengers.child + passengers.adult <= MAX_PAX) {
    pax.child = passengers.child;
    if (passengers.infant + pax.adult + pax.child <= MAX_PAX)
      pax.infant = passengers.infant;
    else pax.infant = MAX_PAX - (pax.adult + pax.child);
  } else {
    pax.child = MAX_PAX - pax.adult;
    pax.infant = 0;
  }
  return pax;
};

export const SNAP_DATA = {
  MAX_PASSENGERS: 6,
  CHILD_AGE_DEFAULT: '1_11',
  INFANT_AGE_DEFAULT: '1_1',
};

export const snapQueryParams = (
  env: string,
  passengers: Passengers,
  originCode: string,
  destinationCode: string,
  departureDate: Date | undefined,
  returnDate: Date | undefined,
  locale: string,
  currency: string,
  promoCode: string | undefined,
  countryCode: string,
) => {
  const listingQueryParams: ListingQueryParams = {};
  const passengerObj = dropPaxToSnap(passengers, SNAP_DATA.MAX_PASSENGERS);
  const children: number[] = [];
  const infants: number[] = [];
  for (let i = 0; i < (passengerObj.child || 0); i++) {
    children.push(Number(SNAP_DATA.CHILD_AGE_DEFAULT));
  }
  for (let i = 0; i < (passengerObj.infant || 0); i++) {
    infants.push(Number(SNAP_DATA.INFANT_AGE_DEFAULT));
  }
  listingQueryParams.orig = originCode;
  listingQueryParams.dest = destinationCode;
  if (departureDate) {
    listingQueryParams.fromDateFlight = DATE_FORMAT(
      departureDate,
      'yyyy-MM-dd',
    );
  }

  if (returnDate) {
    listingQueryParams.toDateFlight = DATE_FORMAT(returnDate, 'yyyy-MM-dd');
  }
  listingQueryParams.adults = passengerObj.adult;
  if (children.length > 0) listingQueryParams.children = children;
  if (infants.length > 0) listingQueryParams.infants = infants;
  listingQueryParams.language = locale;
  listingQueryParams.currency = currency?.toUpperCase();
  listingQueryParams.country_code = countryCode?.toUpperCase();
  listingQueryParams.rooms = 1;
  if (promoCode && promoCode.length > 0)
    listingQueryParams.promoCode = promoCode;
  const queryParams = (
    Object.keys(listingQueryParams) as Array<keyof ListingQueryParams>
  )
    .filter((k) => listingQueryParams[k] !== undefined)
    .map((k) => `${k}=${listingQueryParams[k]}`)
    .join('&');
  return `${EnvironmentURLs.SNAP_REDIRECTION[parseEnv(env)]}?${queryParams}`;
};

export const resolveStation = (
  data: StationDataObject[],
  code: string,
  isCity: boolean,
): StationDataObject | null => {
  if (!data?.length || !code) return null;

  const normalizedCode = code.toUpperCase();

  // Explicit city selection
  if (isCity) {
    return (
      data.find(
        (item) =>
          (stringToBool(item.IsCity) ||
            item.StationType === STATIONS.TYPE.CITY) &&
          item.CityCode?.toUpperCase() === normalizedCode,
      ) ?? null
    );
  }

  // Airport resolution

  // Check top-level airports/Bus/Ferry
  const topLevelAirport = data.find(
    (item) =>
      item.StationType !== STATIONS.TYPE.CITY &&
      item.StationCode?.toUpperCase() === normalizedCode,
  );

  if (topLevelAirport) {
    return topLevelAirport;
  }

  // Search inside city Stations
  for (const item of data) {
    if (item.Stations?.length) {
      const nestedAirport = item.Stations.find(
        (station) => station.StationCode?.toUpperCase() === normalizedCode,
      );

      if (nestedAirport) {
        return nestedAirport;
      }
    }
  }

  // Edge case: CityCode === StationCode (like MAA)
  // If airport wasn't found inside Stations, fallback to city ONLY if no airport exists
  const cityMatch = data.find(
    (item) =>
      (stringToBool(item.IsCity) || item.StationType === STATIONS.TYPE.CITY) &&
      item.CityCode?.toUpperCase() === normalizedCode,
  );

  return cityMatch ?? null;
};

export const MILLISECONDS_IN_HOUR = 60 * 60 * 1000;
export const SHORT_HAUL_MAX_HOURS = 4;
export const JOURNEY_DISTANCE_SHORT_HAUL = 'short_haul';
export const JOURNEY_DISTANCE_LONG_HAUL = 'long_haul';

export const getSafeFormattedDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return isNaN(date.getTime())
    ? ''
    : DATE_FORMAT(date, ANALYTICS_CONSTANTS.DATE_FORMAT);
};

export const calculateRouteType = (
  departureTime?: string,
  arrivalTime?: string,
): string => {
  if (!departureTime || !arrivalTime) return JOURNEY_DISTANCE_SHORT_HAUL;

  const start = new Date(departureTime).getTime();
  const end = new Date(arrivalTime).getTime();

  if (isNaN(start) || isNaN(end)) return JOURNEY_DISTANCE_SHORT_HAUL;

  const differenceInHours = Math.abs(end - start) / (60 * 60 * 1000); // 1 hour in MS

  return differenceInHours <= SHORT_HAUL_MAX_HOURS
    ? JOURNEY_DISTANCE_SHORT_HAUL
    : JOURNEY_DISTANCE_LONG_HAUL;
};

export interface BuildSearchHistoryEntryParams {
  originObj?: StationDataObject;
  destinationObj?: StationDataObject;
  departDate: Date;
  returnDate?: Date;
  tripType: TripType;
  adultCount: number;
  childCount: number;
  infantCount: number;
  promoCode?: string;
}

export const isDepartKiwiStationCheck = (
  departStation?: StationDataObject,
): boolean => departStation?.AAFlight === false;

export const isArrivalKiwiStationCheck = (
  arrivalStation?: StationDataObject,
): boolean => arrivalStation?.AAFlight === false;

/**
 * Builds a single search-history entry for the super app's native bridge
 * (NativeModules.ReactToNative.sendSearchHistory), mirroring the payload
 * shape from the legacy SearchForm.native.js's sendToNative.
 */
export const buildFlightSearchHistoryEntry = ({
  originObj,
  destinationObj,
  departDate,
  returnDate,
  tripType,
  adultCount,
  childCount,
  infantCount,
  promoCode,
}: BuildSearchHistoryEntryParams) => ({
  route: {
    origin: originObj?.StationCode,
    destination: destinationObj?.StationCode,
    originStationName: originObj?.StationName,
    destinationStationName: destinationObj?.StationName,
    originCountry: originObj?.CountryCode,
    destinationCountry: destinationObj?.CountryCode,
    originCityCode: originObj?.CityCode || '',
    destinationCityCode: destinationObj?.CityCode || '',
    IsDepartKiwiStation: isDepartKiwiStationCheck(originObj),
    IsArrivalKiwiStation: isArrivalKiwiStationCheck(destinationObj),
  },
  date: {
    depart: DATE_FORMAT(departDate, ANALYTICS_CONSTANTS.DATE_FORMAT),
    ...(tripType === TripType.ROUND_TRIP && returnDate
      ? { return: DATE_FORMAT(returnDate, ANALYTICS_CONSTANTS.DATE_FORMAT) }
      : {}),
  },
  passengers: {
    adult: adultCount,
    children: childCount,
    infant: infantCount,
  },
  tripType: tripType === TripType.ONE_WAY ? 'O' : 'R',
  createdAt: new Date().toISOString(),
  promoCode: promoCode ?? '',
  isOriginCity: !!originObj?.IsCity,
  isDestinationCity: !!destinationObj?.IsCity,
});
